/**
 * The live-draft strip — turns `10a` (desktop) and `10b` (mobile).
 *
 * "What escalates and what does not. The nav itself never changes — six sections, same order,
 * wherever you are. Only two things move: the Draft item carries a live pill, and the strip above
 * the nav goes from a quiet dark bar stating who is picking, to naming your position in the
 * queue, to red with a single button when the pick is yours."
 *
 * Three things follow from that and are worth stating, because each is a thing the obvious
 * implementation gets wrong:
 *
 *   THE CLOCK SITS NEXT TO THE BUTTON in every state, "so the time and the way back are one
 *   glance". Not at the far left with the status.
 *
 *   THE BUTTON'S LABEL CHANGES with the last state — `Back to draft` becomes `Make the pick`,
 *   "because returning is no longer the point".
 *
 *   ON A PHONE THE STRIP IS AT THE BOTTOM, above the tab bar rather than under the header:
 *   "thumb reach matters more than reading order when the thing you need is a button." Same
 *   markup, positioned by CSS.
 *
 * It renders nothing at all on the draft screens themselves — a strip telling you to go back to
 * the board while you are looking at the board is noise, and the board has its own on-the-clock
 * card three times the size.
 */

import { useEffect, useState } from 'react';
import { managerShort } from '../domain/identity.js';
import { elapsed, type StripInfo } from '../domain/draftStrip.js';
import { PickClock } from './PickClock.js';
import { dockWhen } from './dock.js';
import { STRIP_FIXED, useMedia } from './useMedia.js';

/**
 * The elapsed clock as its own component, with its own one-second timer.
 *
 * Deliberately not a value passed down from the board: the live board renders 160 cells and a
 * rail, and re-rendering all of it every second to move two digits is how a draft-night screen
 * starts feeling slow on somebody's old laptop. Only this span repaints.
 */
export function ElapsedClock({ since, className }: { since: string | null; className?: string }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!since) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [since]);
  const text = elapsed(since, now);
  if (!text) return null;
  return (
    <span className={className} title="How long this pick has been on the clock">{text}</span>
  );
}

export function DraftStrip({ info, onGo }: { info: StripInfo; onGo: () => void }) {
  const fixed = useMedia(STRIP_FIXED);
  if (info.state === 'off' || !info.onClock) return null;

  const yours = info.state === 'yours';

  /*
   * DOCKED, and this one was live-wrong rather than latently wrong. Under 760px the strip is
   * `position: fixed; bottom: var(--tabbar-h)` — it is meant to sit just above the tab bar, where
   * a thumb is. It renders inside `.chrome`, which pull-to-refresh transforms, so "bottom"
   * resolved against the header block instead of the viewport and put the on-the-clock strip a
   * few pixels below the top nav. See `src/ui/dock.tsx`.
   *
   * It does NOT move with a pull now, which is right: it is anchored to the tab bar, and the tab
   * bar does not move either.
   *
   * ONLY THERE, though. Above 760px this bar is an ordinary block inside `.chrome`, which is
   * sticky and does the right thing on its own — and portalling a static element into
   * `document.body` puts it after `#root`, at the bottom of the document. See `dockWhen`.
   */
  return dockWhen(fixed,
    <div className={`dstrip ${info.state}`} role="status">
      {yours && <span className="wipe" aria-hidden="true" />}

      <span className="tag">
        <span className="dot" aria-hidden="true" />
        {yours ? 'YOUR PICK' : 'DRAFT LIVE'}
      </span>

      {/*
        The phone has its own sentence rather than a truncated version of the desktop one — the
        drawing writes "3.04 · Ryan · yours in 14" where the desktop says "Round 3 · 3.04 on the
        clock" and puts the rest in two more elements. At 390px there is room for one clause.
      */}
      {yours ? (
        <span className="said"><code>{info.mine?.code}</code> is yours</span>
      ) : info.state === 'ondeck' ? (
        <>
          <span className="said">
            <span className="wide">
              You are on deck — <code>{info.mine?.code}</code> after{' '}
              {managerShort(info.onClock.owner)}
            </span>
            <span className="narrow">
              On deck — <code>{info.mine?.code}</code> in {info.mine?.away}
            </span>
          </span>
          <span className="aside">
            {info.mine?.away} pick{info.mine?.away === 1 ? '' : 's'} away
          </span>
        </>
      ) : (
        <>
          <span className="said">
            <span className="wide">
              Round {info.onClock.round} · <code>{info.onClock.code}</code> on the clock
            </span>
            <span className="narrow">
              <code>{info.onClock.code}</code> · {managerShort(info.onClock.owner)}
              {info.mine && <> · yours in {info.mine.away}</>}
            </span>
          </span>
          {/* `gmwho`, not `who`: a bare `.who` in this stylesheet carries
              margin-left:auto from the top nav and would float this into the middle. */}
          <span className="gmwho">
            <b>{managerShort(info.onClock.owner)}</b>
          </span>
          {info.mine && (
            <span className="aside">
              your <code>{info.mine.code}</code> is {info.mine.away} picks away
            </span>
          )}
        </>
      )}

      <span className="act">
        {/* Elapsed, not remaining — see the note in domain/draftStrip.ts. */}
        <PickClock since={info.since} seconds={null} size="s" skin="dark" className="clock" />
        <button type="button" className="go" onClick={onGo}>
          {yours ? 'Make the pick' : (
            // 10b shortens this to one word; at 390px "Back to draft" and the clock together
            // leave the sentence about four characters.
            <>
              <span className="wide">Back to draft</span>
              <span className="narrow">Draft</span>
            </>
          )}
        </button>
      </span>
    </div>,
  );
}
